/**
 * Dialog Manager - handles programmatic dialog display
 */

import { createApp, h, ref, computed, type App, type Ref, type VNode } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Checkbox from 'primevue/checkbox'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import PrimeVue from 'primevue/config'
import Ripple from 'primevue/ripple'
import { primeVueThemeOptions, resolveThemePreset } from '@/theme/resolveThemePreset'
import UniDataTable from '@/common/classes/UniDataTable'
import { IKey } from '@/common/constants/ikey'
import { formatFileSizeBytes } from '@/common/utils/fileSize'
import { prepareDialogMessageContent } from '@/common/utils/messageHtmlContent'
import { t } from '@/common/utils/translations'
import { useAppStore } from '@/stores/app'
import type {
  DialogButtonOption,
  DialogOptions,
  FileToSignDialogRow,
  TimeoutRecoveryOptions,
} from './types'
import {
  mapButtonChooserArrayItems,
  resolveButtonChooserChoice,
  type ButtonChooserItem,
} from './buttonChooserContract'

type DialogResolver<T> = (value: T) => void
type DialogClose<T> = (value: T, delayMs?: number) => void
type DialogMountContext<T> = {
  visible: Ref<boolean>
  close: DialogClose<T>
  appendTo: HTMLElement
}
type DialogRenderer<T> = (ctx: DialogMountContext<T>) => () => VNode

/** PrimeVue Portal (appendTo=body) оставляет .p-dialog-mask после unmount — блокирует клики под диалогом платформы. */
export function purgeOrphanBodyDialogMasks(): void {
  if (typeof document === 'undefined') return
  document.querySelectorAll('body > .p-dialog-mask').forEach((mask) => {
    const dialog = mask.querySelector('.p-dialog')
    if (!dialog) {
      mask.remove()
      return
    }
    const ariaHidden = dialog.getAttribute('aria-hidden')
    if (ariaHidden === 'true' || dialog.classList.contains('p-dialog-leave-active')) {
      mask.remove()
    }
  })
}

/** Above LoadingScreen (9999) and overlay dialogs (12000 + mask increment) — otherwise await Messages.* deadlocks under the loader. */
export const MESSAGES_DIALOG_Z_INDEX = 14000

/**
 * Hide MLoader while a blocking Messages dialog is open.
 * Middle onClick keeps the loader modal up; a second PrimeVue modal cannot be dismissed,
 * so executeScript waits until SCRIPT_EXECUTION_TIMEOUT_MS.
 * Call from the main Pinia app, not from the isolated dialog createApp.
 */
export function pauseActionLoading(): () => void {
  try {
    const appStore = useAppStore()
    const wasLoading = Boolean(appStore.isLoading)
    const text = String(appStore.loadingText ?? '')
    if (wasLoading) {
      appStore.setLoading(false)
    }
    return () => {
      if (wasLoading) {
        appStore.setLoading(true, text)
      }
    }
  } catch {
    return () => {}
  }
}

function mountMessagesDialogContainer(): HTMLElement {
  const container = document.createElement('div')
  container.setAttribute('data-messages-dialog-root', 'true')
  container.style.position = 'relative'
  container.style.zIndex = String(MESSAGES_DIALOG_Z_INDEX)
  document.body.appendChild(container)
  return container
}

function setupPrimeVue(app: App) {
  app.use(PrimeVue, {
    theme: {
      preset: resolveThemePreset(),
      options: primeVueThemeOptions,
    },
    ripple: true,
  })
  app.directive('ripple', Ripple)
}

function mountDialogApp<T>(
  resolve: DialogResolver<T>,
  renderDialog: DialogRenderer<T>,
) {
  const visible = ref(true)
  const container = mountMessagesDialogContainer()
  const restoreActionLoading = pauseActionLoading()

  const app: App = createApp({
    setup() {
      return renderDialog({ visible, close, appendTo: container })
    },
  })

  const close: DialogClose<T> = (value, delayMs = 300) => {
    visible.value = false
    setTimeout(() => {
      try {
        app.unmount()
        if (container.parentNode) {
          container.parentNode.removeChild(container)
        }
        purgeOrphanBodyDialogMasks()
      } finally {
        restoreActionLoading()
        resolve(value)
      }
    }, delayMs)
  }

  setupPrimeVue(app)
  app.mount(container)
}

function renderDialogMessageContent(message: string): VNode {
  const prepared = prepareDialogMessageContent(message)
  if (prepared.isHtml) {
    return h('div', {
      class: 'messages-dialog-html',
      innerHTML: prepared.content,
      style: { flex: '1', minWidth: 0, lineHeight: '1.5' },
    })
  }

  return h('div', {
    style: { whiteSpace: 'pre-line', flex: '1', minWidth: 0 },
  }, prepared.content)
}

/**
 * Show a simple confirm dialog
 */
export async function showConfirmDialog(options: DialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const buttons: DialogButtonOption[] = options.buttons && options.buttons.length > 0
      ? options.buttons
      : [
          { type: 'cancel', label: options.cancelText || t('dialogs.cancel'), severity: 'secondary' },
          { type: 'confirm', label: options.confirmText || t('dialogs.ok') }
        ]

    const iconClass = options.iconClass || 'pi pi-exclamation-circle'

    mountDialogApp<boolean>(resolve, ({ visible, close, appendTo }) => {
        const handleConfirm = () => {
          options.onConfirm?.()
          close(true)
        }

        const handleCancel = () => {
          options.onCancel?.()
          close(false)
        }

        return () =>
          h(Dialog, {
            appendTo,
            visible: visible.value,
            modal: true,
            draggable: false,
            baseZIndex: MESSAGES_DIALOG_Z_INDEX,
            class: 'messages-dialog messages-dialog--confirm',
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: options.width || '26rem' }
          }, {
            header: () => h('div', { class: 'messages-dialog__title' }, options.title),
            default: () => h('div', { class: 'messages-dialog__body' }, [
              h('div', {
                class: 'messages-dialog__icon-wrap',
                style: options.iconColor ? { color: options.iconColor } : undefined,
              }, [
                h('i', {
                  class: ['messages-dialog__icon', iconClass],
                  'aria-hidden': 'true',
                }),
              ]),
              h('div', { class: 'messages-dialog__message' }, [
                renderDialogMessageContent(options.message ?? ''),
              ]),
            ]),
            footer: () => h(
              'div',
              { class: 'messages-dialog__footer' },
              buttons.map((button) =>
                h(Button, {
                  label: button.label,
                  severity: button.severity,
                  class: button.type === 'confirm'
                    ? 'messages-dialog__btn messages-dialog__btn--confirm'
                    : 'messages-dialog__btn messages-dialog__btn--cancel',
                  onClick: button.type === 'confirm' ? handleConfirm : handleCancel
                })
              )
            )
          })
    })
  })
}

/** Диалог при таймауте запроса: «Обновить страницу» или «Повторить». */
export function showTimeoutRecoveryDialog(options: TimeoutRecoveryOptions = {}): Promise<'reload' | 'retry'> {
  const title = options.title ?? t('dialogs.timeoutTitle')
  const message = options.message ?? t('dialogs.timeoutMessage')
  const reloadLabel = options.reloadLabel ?? t('dialogs.reloadPage')
  const retryLabel = options.retryLabel ?? t('dialogs.retry')

  return new Promise((resolve) => {
    mountDialogApp<'reload' | 'retry'>(resolve, ({ visible, close, appendTo }) => {
        const handleReload = () => {
          close('reload', 200)
        }

        const handleRetry = () => {
          close('retry', 200)
        }

        return () =>
          h(Dialog, {
            appendTo,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleRetry()
            },
            style: { width: '420px' }
          }, {
            header: () => h('div', title),
            default: () => h('div', { style: { padding: '1rem' } }, message),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.5rem' } }, [
              h(Button, {
                label: retryLabel,
                severity: 'secondary',
                onClick: handleRetry
              }),
              h(Button, {
                label: reloadLabel,
                onClick: handleReload
              })
            ])
          })
    })
  })
}

/**
 * Show an input dialog with textarea
 */
export function showInputTextDialog(
  title: string,
  message: string,
  defaultValue: string = ''
): Promise<string> {
  return new Promise((resolve) => {
    const inputValue = ref(defaultValue)

    // Cancel → "" so meta onClick can safely call .trim() (Swing-compatible empty cancel).
    mountDialogApp<string>(resolve, ({ visible, close, appendTo }) => {
        const handleConfirm = () => {
          close(inputValue.value)
        }

        const handleCancel = () => {
          close('')
        }

        return () =>
          h(Dialog, {
            appendTo,
            visible: visible.value,
            modal: true,
            draggable: false,
            baseZIndex: MESSAGES_DIALOG_Z_INDEX,
            class: 'messages-dialog messages-dialog--input',
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '32rem' }
          }, {
            header: () => h('div', { class: 'messages-dialog__title' }, title),
            default: () => h('div', { class: 'messages-dialog__body messages-dialog__body--stack' }, [
              message ? h('div', { class: 'messages-dialog__prompt' }, message) : null,
              h(Textarea, {
                class: 'messages-dialog__textarea',
                modelValue: inputValue.value,
                'onUpdate:modelValue': (val: string) => {
                  inputValue.value = val
                },
                rows: 6,
                autofocus: true,
              })
            ]),
            footer: () => h('div', { class: 'messages-dialog__footer' }, [
              h(Button, {
                label: t('dialogs.cancel'),
                severity: 'secondary',
                class: 'messages-dialog__btn messages-dialog__btn--cancel',
                onClick: handleCancel
              }),
              h(Button, {
                label: t('dialogs.ok'),
                class: 'messages-dialog__btn messages-dialog__btn--confirm',
                onClick: handleConfirm
              })
            ])
          })
    })
  })
}

/**
 * Show a simple input dialog
 */
export function showInputDialog(
  title: string,
  message: string,
  defaultValue: string = ''
): Promise<string | null> {
  return new Promise((resolve) => {
    const inputValue = ref(defaultValue)

    mountDialogApp<string | null>(resolve, ({ visible, close, appendTo }) => {
        const handleConfirm = () => {
          close(inputValue.value)
        }

        const handleCancel = () => {
          close(null)
        }

        return () =>
          h(Dialog, {
            appendTo,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '400px' }
          }, {
            header: () => h('div', title),
            default: () => h('div', { style: { padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' } }, [
              message ? h('div', { style: { marginBottom: '0.5rem' } }, message) : null,
              h(InputText, {
                modelValue: inputValue.value,
                'onUpdate:modelValue': (val: string) => {
                  inputValue.value = val
                },
                style: { width: '100%' },
                autofocus: true,
                onKeydown: (e: KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    handleConfirm()
                  }
                }
              })
            ]),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.5rem' } }, [
              h(Button, {
                label: t('dialogs.cancel'),
                severity: 'secondary',
                onClick: handleCancel
              }),
              h(Button, {
                label: t('dialogs.ok'),
                onClick: handleConfirm
              })
            ])
          })
    })
  })
}

/**
 * Show checkbox chooser dialog - multi-select with checkboxes
 */
export function showCheckBoxChooserDialog(
  message: string,
  title: string,
  variants: UniDataTable
): Promise<UniDataTable | null> {
  return new Promise((resolve) => {
    const visible = ref(true)
    const selectedItems = ref<Set<string>>(new Set())
    const container = document.createElement('div')
    document.body.appendChild(container)

    // Initialize selected items from variants (where selected = true)
    variants.rows.forEach(row => {
      const name = row.getFieldAsString('name')
      const selected = row.getFieldAsBoolean('selected', false)
      if (selected && name) {
        selectedItems.value.add(name)
      }
    })

    const app = createApp({
      setup() {
        const toggleSelection = (name: string) => {
          if (selectedItems.value.has(name)) {
            selectedItems.value.delete(name)
          } else {
            selectedItems.value.add(name)
          }
        }

        const handleConfirm = () => {
          visible.value = false
          // Create result UniDataTable with selected items
          const result = new UniDataTable()
          selectedItems.value.forEach(name => {
            const row = result.newRow()
            row.setField('name', name)
          })
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(result)
          }, 300)
        }

        const handleCancel = () => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(null)
          }, 300)
        }

        return () =>
          h(Dialog, {
            appendTo: container,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '500px', maxHeight: '600px' }
          }, {
            header: () => h('div', title),
            default: () => h('div', { style: { padding: '1rem', maxHeight: '400px', overflowY: 'auto' } }, [
              message ? h('div', { style: { marginBottom: '1rem' } }, message) : null,
              ...variants.rows.map(row => {
                const name = row.getFieldAsString('name')
                const description = row.getFieldAsString('description', '')
                const isSelected = selectedItems.value.has(name)
                const handleCheckboxClick = (event: Event) => {
                  event.stopPropagation()
                  toggleSelection(name)
                }
                return h('div', {
                  key: name,
                  style: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', cursor: 'pointer' },
                  onClick: () => toggleSelection(name)
                }, [
                  h(Checkbox, {
                    modelValue: isSelected,
                    onClick: handleCheckboxClick,
                    'onUpdate:modelValue': () => {},
                    binary: true
                  }),
                  h('span', description || name)
                ])
              })
            ]),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.5rem' } }, [
              h(Button, {
                label: t('dialogs.cancel'),
                severity: 'secondary',
                onClick: handleCancel
              }),
              h(Button, {
                label: t('dialogs.ok'),
                onClick: handleConfirm
              })
            ])
          })
      }
    })

    app.use(PrimeVue, {
      theme: { preset: resolveThemePreset(), options: primeVueThemeOptions },
      ripple: true
    })
    app.directive('ripple', Ripple)

    app.mount(container)
  })
}

function toTypedFieldsMap(file: any): Record<string, any> {
  if (!file || typeof file !== 'object') {
    return {}
  }

  if (typeof file._getAllFields === 'function') {
    return file._getAllFields() || {}
  }

  if (file.fields && typeof file.fields === 'object') {
    return file.fields
  }

  return {}
}

function toFileToSignRows(files: any[]): FileToSignDialogRow[] {
  const sizeFieldName = (IKey as any).SIZE || 'size'
  return (files || []).map((file, index) => {
    const typedFields = toTypedFieldsMap(file)
    const idRaw = typedFields?.id?.value ?? typedFields?.id ?? index
    const nameRaw = typedFields?.name?.value ?? typedFields?.name ?? t('dialogs.fileFallback', { index: index + 1 })
    const sizeRaw = typedFields?.[sizeFieldName]?.value ?? typedFields?.[sizeFieldName] ?? 0
    const parsedSize = Number(sizeRaw)

    return {
      index,
      id: idRaw != null ? String(idRaw) : String(index),
      name: nameRaw != null ? String(nameRaw) : t('dialogs.fileFallback', { index: index + 1 }),
      size: Number.isFinite(parsedSize) ? parsedSize : 0,
      typedFields,
    }
  })
}

/**
 * Show files-to-sign chooser dialog.
 * Returns UniDataTable with selected files.
 */
export function showFilesToSignChooserDialog(
  title: string,
  files: any[],
  selectedIndices: number[],
  maxSize: number,
  overLimitMessage?: string,
  canBeEmpty: boolean = false,
  showSelectionButtons: boolean = true,
): Promise<UniDataTable | null> {
  return new Promise((resolve) => {
    const visible = ref(true)
    const rows = toFileToSignRows(Array.isArray(files) ? files : [])
    const initialSelected = new Set<number>(
      (Array.isArray(selectedIndices) ? selectedIndices : [])
        .map((x) => Number(x))
        .filter((x) => Number.isInteger(x) && x >= 0 && x < rows.length),
    )
    const selected = ref<Set<number>>(initialSelected)
    const container = document.createElement('div')
    document.body.appendChild(container)
    const app = createApp({
      setup() {
        const totalSelectedSize = computed(() =>
          Array.from(selected.value).reduce((acc, idx) => acc + (rows[idx]?.size ?? 0), 0),
        )
        const hasLimit = Number.isFinite(maxSize) && maxSize > 0
        const isLimitExceeded = computed(() => hasLimit && totalSelectedSize.value > maxSize)

        const allIndices = rows.map((r) => r.index)

        const toggleSelection = (idx: number) => {
          const next = new Set(selected.value)
          if (next.has(idx)) {
            next.delete(idx)
            selected.value = next
            return
          }

          next.add(idx)
          selected.value = next
        }

        const selectAll = () => {
          selected.value = new Set<number>(allIndices)
        }

        const clearSelection = () => {
          selected.value = new Set<number>()
        }

        const close = (result: UniDataTable | null) => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(result)
          }, 250)
        }

        const handleConfirm = async () => {
          if (isLimitExceeded.value) {
            const limitText = formatFileSizeBytes(maxSize)
            const warningMessage = overLimitMessage
              ? String(overLimitMessage).replace('%s', limitText)
              : t('dialogs.signOverLimit', { limit: limitText })
            const agreed = await showConfirmDialog({
              title: t('dialogs.warning'),
              message: warningMessage,
              buttons: [
                { type: 'cancel', label: t('dialogs.no'), severity: 'secondary' },
                { type: 'confirm', label: t('dialogs.yes') },
              ],
            })
            if (!agreed) {
              return
            }
          }
          const result = new UniDataTable()
          Array.from(selected.value)
            .sort((a, b) => a - b)
            .forEach((idx) => {
              const src = rows[idx]
              if (!src) return
              const outRow = result.newRow()
              Object.entries(src.typedFields).forEach(([fieldName, fieldValue]) => {
                if (fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue) {
                  outRow.setFieldWithType(
                    fieldName,
                    (fieldValue as any).value,
                    String((fieldValue as any).type || 'String'),
                  )
                } else {
                  outRow.setField(fieldName, fieldValue)
                }
              })
            })
          close(result)
        }

        const handleCancel = () => close(null)

        return () =>
          h(Dialog, {
            appendTo: container,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '680px', maxHeight: '80vh' },
          }, {
            header: () => h('div', title || t('dialogs.sign')),
            default: () => h('div', { style: { padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' } }, [
              ...(showSelectionButtons
                ? [h('div', { style: { display: 'flex', gap: '0.5rem' } }, [
                    h(Button, { label: t('dialogs.selectAllFiles'), size: 'small', severity: 'secondary', onClick: selectAll }),
                    h(Button, { label: t('dialogs.clearSelection'), size: 'small', severity: 'secondary', onClick: clearSelection }),
                  ])]
                : []),
              h('div', {
                style: {
                  maxHeight: '360px',
                  overflowY: 'auto',
                  border: '1px solid var(--p-content-border-color)',
                  borderRadius: '6px',
                  padding: '0.5rem',
                },
              }, [
                h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' } }, [
                  h('thead', [
                    h('tr', [
                      h('th', { style: { textAlign: 'left', padding: '0.5rem', width: '80px' } }, t('dialogs.selection')),
                      h('th', { style: { textAlign: 'left', padding: '0.5rem' } }, t('dialogs.fileName')),
                      h('th', { style: { textAlign: 'right', padding: '0.5rem', width: '120px' } }, t('dialogs.size')),
                      h('th', { style: { textAlign: 'right', padding: '0.5rem', width: '90px' } }, 'ID'),
                    ]),
                  ]),
                  h('tbody', rows.map((row) => h('tr', {
                    key: row.index,
                    style: { borderTop: '1px solid var(--p-content-border-color)', cursor: 'pointer' },
                    onClick: () => toggleSelection(row.index),
                  }, [
                    h('td', { style: { padding: '0.45rem 0.5rem' } }, [
                      h(Checkbox, {
                        binary: true,
                        modelValue: selected.value.has(row.index),
                        onClick: (event: Event) => {
                          event.stopPropagation()
                          toggleSelection(row.index)
                        },
                        'onUpdate:modelValue': () => {},
                      }),
                    ]),
                    h('td', { style: { padding: '0.45rem 0.5rem' } }, row.name),
                    h('td', { style: { padding: '0.45rem 0.5rem', textAlign: 'right', color: 'var(--p-text-muted-color)' } }, formatFileSizeBytes(row.size)),
                    h('td', { style: { padding: '0.45rem 0.5rem', textAlign: 'right', color: 'var(--p-text-muted-color)' } }, row.id),
                  ]))),
                ]),
              ]),
              h('div', { style: { fontSize: '0.9rem', color: isLimitExceeded.value ? 'var(--p-red-500)' : 'var(--p-text-color)' } }, [
                t('dialogs.selectedCount', {
                  count: selected.value.size,
                  size: formatFileSizeBytes(totalSelectedSize.value),
                }),
                hasLimit ? t('dialogs.ofLimit', { limit: formatFileSizeBytes(maxSize) }) : '',
              ]),
              isLimitExceeded.value
                ? h('div', { style: { color: 'var(--p-red-500)', fontSize: '0.85rem' } }, t('dialogs.sizeExceedsLimit'))
                : null,
            ]),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' } }, [
              h(Button, { label: t('dialogs.cancel'), severity: 'secondary', onClick: handleCancel }),
              h(Button, {
                label: t('dialogs.ok'),
                onClick: () => { void handleConfirm() },
                disabled: !canBeEmpty && selected.value.size === 0,
              }),
            ]),
          })
      },
    })

    app.use(PrimeVue, {
      theme: { preset: resolveThemePreset(), options: primeVueThemeOptions },
      ripple: true,
    })
    app.directive('ripple', Ripple)
    app.mount(container)
  })
}

export function showFilesChooserDialog(
  title: string,
  files: any[],
  selectedIndices: number[],
  maxSize: number,
  overLimitMessage: string,
  canBeEmpty: boolean,
): Promise<UniDataTable | null> {
  return showFilesToSignChooserDialog(
    title,
    files,
    selectedIndices,
    maxSize,
    overLimitMessage,
    canBeEmpty,
    false,
  )
}

/**
 * Show chooser dialog - single selection from array or UniDataTable
 */
export function showChooserDialog(
  message: string,
  title: string,
  variants: any[][] | UniDataTable | Array<{ name: string; value: any }>,
  keyColumn?: string,
  descriptionColumn?: string
): Promise<string | null> {
  return new Promise((resolve) => {
    const visible = ref(true)
    const selectedKey = ref<string | null>(null)
    const container = document.createElement('div')
    document.body.appendChild(container)

    // Convert variants to a common format
    let items: Array<{ key: string; description: string }> = []
    const isMapEntryArray =
      Array.isArray(variants) &&
      variants.length > 0 &&
      variants.every((variant) => variant && typeof variant === 'object' && !Array.isArray(variant) && 'name' in variant && 'value' in variant)

    if (variants instanceof UniDataTable) {
      // From UniDataTable
      items = variants.rows.map(row => ({
        key: String(row.getFieldAsObject(keyColumn || 'key') || row.getFieldAsString(keyColumn || 'key') || ''),
        description: String(row.getFieldAsObject(descriptionColumn || 'description') || row.getFieldAsString(descriptionColumn || 'description') || '')
      }))
    } else if (isMapEntryArray) {
      items = (variants as Array<{ name: string; value: any }>).map((variant) => ({
        key: String(variant.name ?? ''),
        description: String(variant.value ?? variant.name ?? ''),
      }))
    } else if (Array.isArray(variants)) {
      items = (variants as any[][]).map((variant) => ({
        key: String(variant?.[0] ?? ''),
        description: String(variant?.[1] ?? variant?.[0] ?? ''),
      }))
    }

    const app = createApp({
      setup() {
        const handleConfirm = () => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(selectedKey.value)
          }, 300)
        }

        const handleCancel = () => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(null)
          }, 300)
        }

        return () =>
          h(Dialog, {
            appendTo: container,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '500px', maxHeight: '600px' }
          }, {
            header: () => h('div', title),
            default: () => h('div', { style: { padding: '1rem', maxHeight: '400px', overflowY: 'auto' } }, [
              message ? h('div', { style: { marginBottom: '1rem' } }, message) : null,
              h('select', {
                value: selectedKey.value ?? '',
                style: {
                  width: '100%',
                  height: '2.5rem',
                  borderRadius: '6px',
                  border: '1px solid var(--p-content-border-color)',
                  padding: '0 0.6rem',
                  backgroundColor: 'var(--p-content-background)',
                },
                onChange: (event: Event) => {
                  const target = event.target as HTMLSelectElement
                  selectedKey.value = target.value
                },
              }, [
                h('option', { value: '', disabled: true }, t('dialogs.selectValue')),
                ...items.map((item) =>
                  h('option', { key: item.key, value: item.key }, item.description),
                ),
              ]),
            ]),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.5rem' } }, [
              h(Button, {
                label: t('dialogs.cancel'),
                severity: 'secondary',
                onClick: handleCancel
              }),
              h(Button, {
                label: t('dialogs.ok'),
                onClick: handleConfirm,
                disabled: selectedKey.value === null || selectedKey.value === ''
              })
            ])
          })
      }
    })

    app.use(PrimeVue, {
      theme: { preset: resolveThemePreset(), options: primeVueThemeOptions },
      ripple: true
    })
    app.directive('ripple', Ripple)

    app.mount(container)
  })
}

/**
 * Show button chooser dialog.
 * Swing: variant click closes immediately with that key; X/mask → null. No OK/Cancel footer.
 */
export function showButtonChooserDialog(
  message: string,
  title: string,
  variants: any[][] | UniDataTable,
  keyColumn?: string,
  descriptionColumn?: string
): Promise<string | null> {
  let items: ButtonChooserItem[] = []

  if (variants instanceof UniDataTable) {
    items = variants.rows.map(row => ({
      key: String(row.getFieldAsObject(keyColumn || 'key') || row.getFieldAsString(keyColumn || 'key') || ''),
      description: String(row.getFieldAsObject(descriptionColumn || 'description') || row.getFieldAsString(descriptionColumn || 'description') || '')
    }))
  } else {
    items = mapButtonChooserArrayItems(variants)
  }

  return new Promise((resolve) => {
    let settled = false
    mountDialogApp<string | null>(resolve, ({ visible, close, appendTo }) => {
      const finish = (value: string | null) => {
        if (settled) return
        settled = true
        close(value)
      }
      return () =>
        h(Dialog, {
          appendTo,
          visible: visible.value,
          modal: true,
          draggable: false,
          baseZIndex: MESSAGES_DIALOG_Z_INDEX,
          class: 'messages-dialog messages-dialog--button-chooser',
          'onUpdate:visible': (val: boolean) => {
            if (!val) finish(resolveButtonChooserChoice({ kind: 'dismiss' }))
          },
          style: { width: '500px' }
        }, {
          header: () => h('div', { class: 'messages-dialog__title' }, title),
          default: () => h('div', {
            class: 'messages-dialog__body',
            style: { padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
          }, [
            message ? renderDialogMessageContent(message) : null,
            h('div', {
              style: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
            }, items.map(item =>
              h(Button, {
                key: item.key,
                label: item.description,
                severity: 'secondary',
                outlined: true,
                class: 'messages-dialog__btn',
                onClick: () => finish(resolveButtonChooserChoice({ kind: 'choose', key: item.key })),
              })
            )),
          ]),
        })
    })
  })
}

/**
 * Show button chooser by columns dialog.
 * Swing: variant click closes immediately with that key; X/mask → null. No OK/Cancel footer.
 */
export function showButtonChooserByColumnsDialog(
  message: string,
  title: string,
  variants: any[][],
  buttonsOnRow: number
): Promise<string | null> {
  const columnsPerRow = Math.max(1, Number(buttonsOnRow) || 1)

  const unwrapValue = (value: any): any => {
    if (value && typeof value === 'object' && 'value' in value) {
      return (value as any).value
    }
    return value
  }

  const items: ButtonChooserItem[] = (Array.isArray(variants) ? variants : []).map((variant, index) => {
    const rawKey = unwrapValue((variant as any)?.name ?? (variant as any)?.key ?? (variant as any)?.id)
    const rawDescription = unwrapValue(
      (variant as any)?.value ?? (variant as any)?.description ?? (variant as any)?.label ?? rawKey
    )
    return {
      key: rawKey == null || rawKey === '' ? `btn_${index}` : String(rawKey),
      description: String(rawDescription ?? ''),
    }
  })

  const buttonRows: ButtonChooserItem[][] = []
  for (let i = 0; i < items.length; i += columnsPerRow) {
    buttonRows.push(items.slice(i, i + columnsPerRow))
  }

  return new Promise((resolve) => {
    let settled = false
    mountDialogApp<string | null>(resolve, ({ visible, close, appendTo }) => {
      const finish = (value: string | null) => {
        if (settled) return
        settled = true
        close(value)
      }
      return () =>
        h(Dialog, {
          appendTo,
          visible: visible.value,
          modal: true,
          draggable: false,
          baseZIndex: MESSAGES_DIALOG_Z_INDEX,
          class: 'messages-dialog messages-dialog--button-chooser',
          'onUpdate:visible': (val: boolean) => {
            if (!val) finish(resolveButtonChooserChoice({ kind: 'dismiss' }))
          },
          style: { width: '600px' }
        }, {
          header: () => h('div', { class: 'messages-dialog__title' }, title),
          default: () => h('div', {
            class: 'messages-dialog__body',
            style: { padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
          }, [
            message ? renderDialogMessageContent(message) : null,
            ...buttonRows.map((row, rowIdx) =>
              h('div', {
                key: rowIdx,
                style: { display: 'grid', gridTemplateColumns: `repeat(${columnsPerRow}, minmax(0, 1fr))`, gap: '0.5rem' }
              }, row.map((item) =>
                h(Button, {
                  key: item.key,
                  label: item.description,
                  severity: 'secondary',
                  outlined: true,
                  class: 'messages-dialog__btn',
                  onClick: () => finish(resolveButtonChooserChoice({ kind: 'choose', key: item.key })),
                })
              ))
            )
          ]),
        })
    })
  })
}

/**
 * Show chooser with table dialog
 */
export function showChooserWithTableDialog(
  message: string,
  title: string,
  variants: UniDataTable,
  descriptionColumn: string[]
): Promise<string | null> {
  return new Promise((resolve) => {
    const visible = ref(true)
    const selectedKey = ref<string | null>(null)
    const container = document.createElement('div')
    document.body.appendChild(container)

    // Extract key column (first column that's not in descriptionColumn)
    const allColumns = variants.rows.length > 0 ? Object.keys(variants.rows[0].fields) : []
    const keyColumn = allColumns.find(col => !descriptionColumn.includes(col)) || allColumns[0] || 'key'

    // Prepare table data
    const tableData = variants.rows.map(row => {
      const rowData: any = { _key: row.getFieldAsString(keyColumn) }
      descriptionColumn.forEach(col => {
        rowData[col] = row.getFieldAsString(col)
      })
      return rowData
    })

    const app = createApp({
      setup() {
        const handleConfirm = () => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(selectedKey.value)
          }, 300)
        }

        const handleCancel = () => {
          visible.value = false
          setTimeout(() => {
            app.unmount()
            document.body.removeChild(container)
            purgeOrphanBodyDialogMasks()
            resolve(null)
          }, 300)
        }

        return () =>
          h(Dialog, {
            appendTo: container,
            visible: visible.value,
            modal: true,
            'onUpdate:visible': (val: boolean) => {
              if (!val) handleCancel()
            },
            style: { width: '700px', maxHeight: '600px' }
          }, {
            header: () => h('div', title),
            default: () => h('div', { style: { padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '450px', overflow: 'auto' } }, [
              message ? h('div', { style: { marginBottom: '0.5rem' } }, message) : null,
              h(DataTable, {
                value: tableData,
                selectionMode: 'single',
                selection: tableData.find(item => item._key === selectedKey.value),
                'onUpdate:selection': (selection: any) => {
                  selectedKey.value = selection?._key || null
                },
                dataKey: '_key',
                style: { width: '100%' }
              }, {
                default: () => descriptionColumn.map(col =>
                  h(Column, {
                    key: col,
                    field: col,
                    header: col,
                    style: { minWidth: '150px' }
                  })
                )
              })
            ]),
            footer: () => h('div', { style: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', padding: '0.5rem' } }, [
              h(Button, {
                label: t('dialogs.cancel'),
                severity: 'secondary',
                onClick: handleCancel
              }),
              h(Button, {
                label: t('dialogs.ok'),
                onClick: handleConfirm,
                disabled: selectedKey.value === null
              })
            ])
          })
      }
    })

    app.use(PrimeVue, {
      theme: { preset: resolveThemePreset(), options: primeVueThemeOptions },
      ripple: true
    })
    app.directive('ripple', Ripple)

    app.mount(container)
  })
}
